import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowSquareOut, CheckCircle, CircleNotch, Globe } from '@phosphor-icons/react';
import { portfolioApi, type PortfolioState } from '@/lib/api/portfolio';
import { tenantApi } from '@/lib/api/tenant';
import { queryKeys } from '@/lib/api/queryKeys';
import { portfolioLabel, publicPortfolioUrl } from '@/lib/config';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  portfolio: PortfolioState | undefined;
}

export function PublishDialog({ open, onOpenChange, portfolio }: Props) {
  const qc = useQueryClient();
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    staleTime: 60_000,
  });

  const isPublished = Boolean(portfolio?.publishedAt);

  const mutation = useMutation({
    mutationFn: () => (isPublished ? portfolioApi.unpublish() : portfolioApi.publish()),
    onSuccess: (next) => {
      qc.setQueryData(queryKeys.portfolio, next);
      if (next.publishedAt) {
        toast.success('Your site is live', {
          description: tenantQuery.data?.subdomain
            ? portfolioLabel(tenantQuery.data.subdomain)
            : undefined,
          icon: <CheckCircle size={14} />,
        });
      } else {
        toast.success('Moved to draft', {
          description: 'Your site is no longer public.',
        });
      }
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('Publish failed', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    },
  });

  const liveUrl = tenantQuery.data?.subdomain
    ? publicPortfolioUrl(tenantQuery.data.subdomain)
    : '';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-line bg-board p-6 shadow-float">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-canvas text-ink-muted">
              <Globe size={16} />
            </span>
            <div className="flex-1">
              <Dialog.Title className="font-display text-lg font-semibold tracking-tight">
                {isPublished ? 'Unpublish portfolio?' : 'Publish your portfolio?'}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-muted">
                {isPublished
                  ? 'Your site will stop appearing publicly. Drafts are kept.'
                  : 'Your portfolio will be visible to anyone with the link.'}
              </Dialog.Description>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-line bg-canvas p-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              Live URL
            </p>
            <p className="mt-1 break-all font-mono text-sm text-ink">{liveUrl || '—'}</p>
          </div>

          <div className="mt-6 flex items-center justify-between">
            {isPublished && liveUrl ? (
              <a
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-ink underline underline-offset-4"
              >
                Open current live site <ArrowSquareOut size={12} />
              </a>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-md border border-line bg-board px-3 py-2 text-xs font-medium text-ink hover:bg-rail"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={!portfolio || mutation.isPending}
                onClick={() => mutation.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs font-medium text-board transition-colors hover:bg-ink/90 disabled:opacity-60"
              >
                {mutation.isPending ? (
                  <>
                    <CircleNotch size={12} className="animate-spin" />
                    Working…
                  </>
                ) : isPublished ? (
                  'Move to draft'
                ) : (
                  <>
                    <Globe size={12} />
                    Publish now
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
